// Fork: shared types, routing base and formatting for the public status page.
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

export type StatusLevel = 'operational' | 'degraded' | 'outage'

export interface StatusBranding {
  title: string
  description: string
  logo_url: string
  accent_color: string
}

export interface StatusUpdate {
  label?: string
  message: string
  created_at: string
}

export interface StatusAffectedComponent {
  key: string
  name: string
  level: StatusLevel
}

export interface StatusIncidentDetail {
  id: string
  title: string
  severity: string
  level: StatusLevel
  status: string
  triggered_at: string
  resolved_at?: string
  components: StatusAffectedComponent[]
  updates: StatusUpdate[]
}

export const LEVEL_LABEL: Record<StatusLevel, string> = {
  operational: 'Operational',
  degraded: 'Degraded performance',
  outage: 'Outage',
}

export const LEVEL_BAR_CLASS: Record<StatusLevel, string> = {
  operational: 'bg-green-500',
  degraded: 'bg-yellow-400',
  outage: 'bg-red-500',
}

export const LEVEL_ICON_CLASS: Record<StatusLevel, string> = {
  operational: 'text-green-600',
  degraded: 'text-yellow-500',
  outage: 'text-red-600',
}

/**
 * Path prefix of the status page: '' on the dedicated status host and
 * '/status' inside the main app.
 */
export const StatusBaseContext = createContext('')

/** Returns a function that prefixes a status page path with the current base. */
export function useStatusHref(): (path: string) => string {
  const base = useContext(StatusBaseContext)
  return useCallback((path: string) => (path === '/' ? base || '/' : `${base}${path}`), [base])
}

export function isResolved(incident: { status: string }): boolean {
  return incident.status === 'resolved'
}

/** Heading for an incident's current state on public pages. */
export function incidentStateLabel(incident: { status: string }): string {
  return isResolved(incident) ? 'Resolved' : 'Ongoing'
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export function formatTimeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/** Formats a whole number of minutes as "N minutes", "N hours" or "N days". */
export function formatSpan(totalMinutes: number): string {
  const minutes = Math.max(0, Math.round(totalMinutes))
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'}`
}

export function timeAgo(iso: string, now: number = Date.now()): string {
  const minutes = (now - new Date(iso).getTime()) / 60_000
  return minutes < 1 ? 'just now' : `${formatSpan(minutes)} ago`
}

interface StatusSummary {
  org_name: string
  branding: StatusBranding
}

/** Loads branding for status subpages; null until loaded or on failure. */
export function useStatusBranding(): StatusSummary | null {
  const [summary, setSummary] = useState<StatusSummary | null>(null)
  useEffect(() => {
    let cancelled = false
    window.fetch('/api/v1/status')
      .then(res => (res.ok ? res.json() : null))
      .then((json: StatusSummary | null) => { if (!cancelled && json?.branding) setSummary(json) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])
  useEffect(() => {
    if (summary?.branding.title) document.title = summary.branding.title
  }, [summary?.branding.title])
  return summary
}
